import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { StreamableFile } from "@nestjs/common";
import {
    ImporterErrors,
    DocumentTaxonomyExtractionSchema,
} from "@common/types/financialDocument";
import { BalanceSheetAccountSchema } from "@common/dto/financialDocument/balance-sheet-extraction.schema";
import {
    BatchUpdateBillExtractionRowsBodySchema,
    BatchUpdateInvoiceExtractionRowsBodySchema,
    UpdateAccrualExtractionDetailsBodySchema,
    UpdateBillExtractionDetailsBodySchema,
} from "@common/schemas/financialDocument/accrual-extraction-incremental-update.schema";
import {
    UniqueBillExtractionRecordsSchema,
    UniqueInvoiceExtractionRecordsSchema,
} from "@common/schemas/financialDocument/accrual-extraction-records-validation";
import {
    FinancialDocumentBulkActionSchema,
    FinancialDocumentBulkFiltersSchema,
} from "@common/schemas/financialDocument/financial-document-bulk-action.schema";
import { LinkedDocumentFieldUpdateErrorSchema } from "@common/schemas/financialDocument/linked-document-field-update-error.schema";
import { InvalidDocumentCategoryErrorSchema } from "@common/schemas/financialDocument/invalid-document-category-error.schema";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    confirmAccrualCounterpartiesBodySchema,
    confirmAccrualCounterpartiesResponseSchema,
    finalizeAccrualImportBodySchema,
} from "@common/schemas/financialDocument/financial-document-accrual-import.schema";
import { RemovingDocumentWithTransactionsErrorSchema } from "@common/schemas/financialDocument/removing-document-with-transactions-error.schema";
import { RemovingDocumentUsedInReconciliationErrorSchema } from "@common/schemas/financialDocument/removing-document-used-in-reconciliation-error.schema";
import { ManualJournalEntryExistsErrorSchema } from "@common/schemas/financialDocument/manual-journal-entry-exists-error.schema";
import { UpdateDocumentCategoryBodySchema } from "@common/schemas/financialDocument/update-document-category.schema";
import { UpdateDocumentCounterpartyBodySchema } from "@common/schemas/financialDocument/update-document-counterparty.schema";
import { UpdateAccountInfoBodySchema } from "@common/schemas/financialDocument/update-account-info.schema";
import { FinancialDocumentQuerySchema } from "@common/schemas/financialDocument/financial-document-query.schema";
import { FinancialDocumentDetailIncludeSchema } from "@common/schemas/financialDocument/financial-document-detail-include.schema";
import { AccountInfoExtractionSchema } from "@common/schemas/financialDocument/account-info-extraction.schema";
import {
    mcpRequestUploadBodySchema,
    mcpConfirmUploadBodySchema,
    mcpDownloadDocumentBodySchema,
} from "@common/schemas/financialDocument/financial-document-mcp.schema";
import { fetchFinancialDocumentsResponseSchema } from "@common/schemas/financialDocument/financial-document-list-item.schema";
import { uploadFinancialDocumentBodySchema } from "@common/schemas/financialDocument/upload-financial-document.schema";
import { FinancialDocumentExtendedDto } from "../dto/financialDocument/financial-document-extended.dto";
import { FinancialDocumentDto } from "../dto/financialDocument/financial-document.dto";

const c = initContract();

export const financialDocumentContract = c.router(
    {
        getDocuments: {
            method: "GET",
            path: "/",
            query: FinancialDocumentQuerySchema,
            responses: {
                200: fetchFinancialDocumentsResponseSchema,
                403: c.noBody(),
            },
        },
        uploadDocument: {
            method: "POST",
            path: "/",
            body: uploadFinancialDocumentBodySchema,
            responses: {
                201: c.type<FinancialDocumentExtendedDto>(),
                400: ErrorMessageSchema,
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        getDocument: {
            method: "GET",
            path: "/:financialDocumentId",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            query: FinancialDocumentDetailIncludeSchema,
            responses: {
                200: c.type<FinancialDocumentExtendedDto>(),
                400: ErrorMessageSchema,
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        getDocumentFile: {
            method: "GET",
            path: "/:financialDocumentId/file",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            responses: {
                200: c.type<StreamableFile>(),
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        deleteDocument: {
            method: "DELETE",
            path: "/:financialDocumentId",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            query: z.object({
                confirmTransactionsRemoval: z.coerce.boolean().optional(),
            }),
            responses: {
                204: c.noBody(),
                400: z.union([
                    RemovingDocumentWithTransactionsErrorSchema,
                    RemovingDocumentUsedInReconciliationErrorSchema,
                ]),
                409: ManualJournalEntryExistsErrorSchema,
            },
        },
        createUploadUrl: {
            method: "POST",
            path: "/upload-url",
            body: z.object({
                path: z.string(),
            }),
            responses: {
                200: z.object({
                    url: z.string(),
                }),
            },
        },
        uploadDocumentS3: {
            method: "POST",
            path: "/s3",
            body: z.object({
                workspaceId: z.string(),
                path: z.string(),
            }),
            responses: {
                200: c.type<FinancialDocumentExtendedDto[]>(),
            },
        },
        addToImporter: {
            method: "POST",
            path: "/:financialDocumentId/add-to-importer",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: z.object({
                importerQueue: z.string().optional(),
                reconciliedFinancialAccountId: z.number().optional(),
                forceRerun: z.boolean().optional(),
            }),
            responses: {
                200: c.type<FinancialDocumentExtendedDto>(),
            },
        },
        updateDocumentMetadata: {
            method: "PATCH",
            path: "/:financialDocumentId/metadata",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: z.object({
                openingBalance: z.number().nullable().optional(),
                openingDate: z.string().date().optional(),
                closingBalance: z.number().nullable().optional(),
                closingDate: z.string().date().optional(),
                fileName: z.string().optional(),
                financialAccountId: z.number().nullable().optional(),
                entityId: z.number().nullable().optional(),
            }),
            responses: {
                200: c.type<FinancialDocumentDto>(),
                400: z.object({ message: z.string() }),
                404: c.noBody(),
            },
        },
        updateTaxonomyExtraction: {
            method: "PATCH",
            path: "/:financialDocumentId/taxonomy-extraction-fields",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: DocumentTaxonomyExtractionSchema,
            responses: {
                200: c.type<FinancialDocumentExtendedDto>(),
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        batchUpdateBalanceSheetExtractionRows: {
            method: "POST",
            path: "/:financialDocumentId/balance-sheet-extraction/rows/batch",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: z.object({
                upserts: z
                    .array(
                        BalanceSheetAccountSchema.extend({
                            rowIndex: z.number().int().min(0).max(499),
                        }),
                    )
                    .max(500),
                deletes: z.array(z.number().int().min(0).max(499)).max(500),
            }),
            responses: {
                200: c.noBody(),
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        updateBalanceSheetExtractionDetails: {
            method: "PATCH",
            path: "/:financialDocumentId/balance-sheet-extraction/details",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: z.object({
                date: z.string().date().nullable().optional(),
                warnings: z
                    .array(z.string().max(500))
                    .max(50)
                    .nullable()
                    .optional(),
            }),
            responses: {
                200: c.noBody(),
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        updateInvoiceExtractions: {
            method: "PATCH",
            path: "/:financialDocumentId/invoice-extractions",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: z.object({
                invoiceExtractions: UniqueInvoiceExtractionRecordsSchema,
            }),
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        updateBillExtractions: {
            method: "PATCH",
            path: "/:financialDocumentId/bill-extractions",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: z.object({
                billExtractions: UniqueBillExtractionRecordsSchema,
            }),
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        batchUpdateInvoiceExtractionRows: {
            method: "POST",
            path: "/:financialDocumentId/invoice-extractions/:invoiceExtractionId/rows/batch",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
                invoiceExtractionId: z.string().uuid(),
            }),
            body: BatchUpdateInvoiceExtractionRowsBodySchema,
            responses: {
                200: c.noBody(),
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        updateInvoiceExtractionDetails: {
            method: "PATCH",
            path: "/:financialDocumentId/invoice-extractions/:invoiceExtractionId/details",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
                invoiceExtractionId: z.string().uuid(),
            }),
            body: UpdateAccrualExtractionDetailsBodySchema,
            responses: {
                200: c.noBody(),
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        batchUpdateBillExtractionRows: {
            method: "POST",
            path: "/:financialDocumentId/bill-extractions/:billExtractionId/rows/batch",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
                billExtractionId: z.string().uuid(),
            }),
            body: BatchUpdateBillExtractionRowsBodySchema,
            responses: {
                200: c.noBody(),
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        updateBillExtractionDetails: {
            method: "PATCH",
            path: "/:financialDocumentId/bill-extractions/:billExtractionId/details",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
                billExtractionId: z.string().uuid(),
            }),
            body: UpdateBillExtractionDetailsBodySchema,
            responses: {
                200: c.noBody(),
                403: c.noBody(),
                404: c.noBody(),
            },
        },
        removeImporterError: {
            method: "PATCH",
            path: "/:financialDocumentId/importer-errors",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: z.object({
                toRemove: z.nativeEnum(ImporterErrors),
            }),
            responses: {
                200: c.type<FinancialDocumentExtendedDto>(),
            },
        },
        checkForImporterErrors: {
            method: "GET",
            path: "/:financialDocumentId/check-for-importer-errors",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            query: z.object({
                checkTransactions: z.coerce.boolean().optional(),
            }),
            responses: {
                200: c.type<FinancialDocumentExtendedDto>(),
            },
        },
        finalizeTransactionsImport: {
            method: "POST",
            path: "/finalize-transactions-import",
            body: z.object({
                workspaceId: z.string(),
                financialDocumentIds: z.array(z.number()),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        finalizeInvoicesImport: {
            method: "POST",
            path: "/finalize-invoices-import",
            body: finalizeAccrualImportBodySchema,
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        finalizeBillsImport: {
            method: "POST",
            path: "/finalize-bills-import",
            body: finalizeAccrualImportBodySchema,
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        confirmAccrualCounterparties: {
            method: "POST",
            path: "/confirm-accrual-counterparties",
            body: confirmAccrualCounterpartiesBodySchema,
            responses: {
                200: confirmAccrualCounterpartiesResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateDocumentCategory: {
            method: "PATCH",
            path: "/:financialDocumentId/category",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: UpdateDocumentCategoryBodySchema,
            responses: {
                200: c.type<FinancialDocumentExtendedDto>(),
                404: c.noBody(),
            },
        },
        updateDocumentCounterparty: {
            method: "PATCH",
            path: "/:financialDocumentId/counterparty",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: UpdateDocumentCounterpartyBodySchema,
            responses: {
                200: c.type<FinancialDocumentExtendedDto>(),
                400: ErrorMessageSchema,
                404: c.noBody(),
            },
        },
        removeFromImporter: {
            method: "DELETE",
            path: "/:financialDocumentId/importer",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
            },
        },
        bulkAction: {
            method: "POST",
            path: "/bulk-action",
            body: z.object({
                workspaceId: z.string().uuid(),
                action: FinancialDocumentBulkActionSchema,
                financialDocumentIds: z.array(z.number()).optional(),
                filters: FinancialDocumentBulkFiltersSchema.optional(),
            }),
            responses: {
                204: c.noBody(),
                400: z.union([
                    RemovingDocumentWithTransactionsErrorSchema,
                    RemovingDocumentUsedInReconciliationErrorSchema,
                    InvalidDocumentCategoryErrorSchema,
                    LinkedDocumentFieldUpdateErrorSchema,
                ]),
                409: ManualJournalEntryExistsErrorSchema,
            },
        },
        download: {
            method: "POST",
            path: "/download",
            body: z.object({
                workspaceId: z.string().uuid(),
                financialDocumentIds: z.array(z.number()).optional(),
                filters: FinancialDocumentBulkFiltersSchema.optional(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        mcpRequestUploadUrl: {
            method: "POST",
            path: "/mcp-upload-url",
            body: mcpRequestUploadBodySchema,
            responses: {
                200: z.object({
                    uploadUrl: z.string(),
                    uploadToken: z.string(),
                    instructions: z.string(),
                }),
                400: z.object({
                    error: z.string(),
                }),
            },
        },
        mcpConfirmUpload: {
            method: "POST",
            path: "/mcp-confirm-upload",
            body: mcpConfirmUploadBodySchema,
            responses: {
                200: z.object({
                    documentId: z.number(),
                    status: z.string(),
                    fileName: z.string(),
                }),
                400: z.object({
                    error: z.string(),
                }),
                404: z.object({
                    error: z.string(),
                }),
            },
        },
        mcpDownloadDocument: {
            method: "POST",
            path: "/mcp-download",
            body: mcpDownloadDocumentBodySchema,
            responses: {
                200: z.object({
                    downloadUrl: z.string(),
                    fileName: z.string(),
                    documentId: z.number(),
                }),
                404: z.object({
                    error: z.string(),
                }),
            },
        },
        updateAccountInfo: {
            method: "PATCH",
            path: "/:financialDocumentId/account-info",
            pathParams: z.object({
                financialDocumentId: z.coerce.number(),
            }),
            body: UpdateAccountInfoBodySchema,
            responses: {
                200: AccountInfoExtractionSchema,
                404: c.noBody(),
            },
        },
    },
    {
        pathPrefix: "/financial-document",
    },
);
